/* eslint-disable react-hooks/exhaustive-deps */
import { Text, Heading, Flex } from '@chakra-ui/react';
import { RaceParticipants, Races, RaceTipsResponse } from 'api/generated/graphql';

import { useGetRaceResultsById, useGetRaceTips } from 'hooks';
import { RLoadingAnimation, RSpiderChart } from 'components';
import { IconEmptySet } from 'icons';
import { CarPerformanceStats } from 'components/PerformanceModal/CarPerformanceStats';
import { getSpiderData } from 'components/PerformanceModal/ChartData';
import TuningComparisons from 'components/TuningComparisons/TuningComparisons';

// TODO refactor copy/pasta from Performance Modal into shareable code
const Performance = ({
  race,
  currentPlayerId, // note this is participant ID from the race (not user ID)
  carId
}: {
  race: Races;
  currentPlayerId: string;
  carId?: string;
}) => {
  const { data: raceData, isLoading: isLoadingRaceResults } =
    useGetRaceResultsById(race.id);
  const results = raceData?.getRaceResults;
  const playerAtPosition = (position: number) => {
    return results?.playersParticipants.find((p) => p.position === position);
  };
  const firstPlacePlayer = playerAtPosition(1);

  // if the current user placed 1st, compare to 2nd place
  // if the current user didn't place first, compare to 1st place
  const comparisonPlayer =
    firstPlacePlayer?.id === currentPlayerId
      ? playerAtPosition(2)
      : results?.playersParticipants.find((p) => p.id === currentPlayerId);

  const { data: raceTipsData, isLoading: isLoadingRaceTips } = useGetRaceTips(
    race.id,
    carId
  );

  const isLoading = isLoadingRaceResults || isLoadingRaceTips || firstPlacePlayer === undefined || comparisonPlayer === undefined;

  const getScores = (participant: RaceParticipants) => {
    return [
      participant?.topSpeedScore,
      participant?.highSpeedScore,
      participant?.lowSpeedScore,
      participant?.brakingScore,
      participant?.coolingScore,
      (participant?.handlingScore || 0) / 50,
    ];
  };

  const formatScores = (scores: number[]) => scores.map(score => parseFloat(score.toFixed(1)));
  
  const chartData = getSpiderData({
    data1: formatScores(getScores(firstPlacePlayer as RaceParticipants).filter(score => score !== undefined) as number[]),
    data2: formatScores(getScores(comparisonPlayer as RaceParticipants).filter(score => score !== undefined) as number[]),
  });

  if (isLoading) {
    return (
      <Flex w="full" h="full" alignItems="center" justifyContent="center">
        <RLoadingAnimation />
      </Flex>
    );
  }

  return (
    <>
      {!race.userInRace && (
        <Flex
          width="full"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="80%"
        >
          <IconEmptySet mb="1rem" />
          <Heading as="h2" size="md" textTransform="uppercase">
            No Results to See Here
          </Heading>
        </Flex>
      )}

      {race.userInRace && firstPlacePlayer && comparisonPlayer && (
        <>
          <Flex gap={4} w="full" px={0} py={8} flexDir={{ base: 'column', md: 'row' }}>
            <CarPerformanceStats
              player={firstPlacePlayer as RaceParticipants}
              comparisonPlayer={comparisonPlayer as RaceParticipants}
              spiderData={chartData}
              currentPlayerId={currentPlayerId}
              style="comparisson"
              tipsForTyre={raceTipsData?.getRaceTips?.tipsForTyre || ''}
            />

            <TuningComparisons 
              raceTipsData={raceTipsData?.getRaceTips as unknown as RaceTipsResponse}
              raceLevel={race.tier.name.toLowerCase() === 'tournament' || race.tier.name.toLowerCase() === 'weeklytournament' ? 6 : race.difficulty + 1}
              tipsForMetrics={raceTipsData?.getRaceTips?.tipsForMetrics || {}}
              userWon={firstPlacePlayer.id === currentPlayerId}
              userPosition={
                firstPlacePlayer.id === currentPlayerId 
                ? firstPlacePlayer.position || undefined  
                : comparisonPlayer.position || undefined
              }
              competitorPosition={
                comparisonPlayer.id === currentPlayerId 
                ? firstPlacePlayer.position || undefined 
                : comparisonPlayer.position || undefined
              }
            />
          </Flex>
        </>
      )}
    </>
  );
};

export default Performance;
